import { Box, Button, Grid, TextField, Typography } from "@mui/material"
import dayjs from "dayjs"
import React, { useEffect, useState } from "react"
import ModalDialogPage from "src/components/modal-dialog"
import TableGray from "src/components/table/table-gray"
import EmissaoService from "src/services/emissao-service"
import { DATE_FORMAT, DATE_FORMAT_SHOW } from "src/utils/constants"
import PropTypes from 'prop-types'
import SkeletonLine from "src/components/skeleton-line"
import { ordenaPorDataReferencia } from "src/utils/date-format"
import ParameterTable from "src/components/table/parameter-table"
import { calculateMinWidth, renderCellDate, renderTextColumn } from "src/utils/table-render-functions"
import AlertMessage from "src/components/alert-message"

const defaultTable = [
    {
        cells: ["CETIP: ", "ISIN: "]
    }
]

const UltimoEventoCreditoEmissao = ({ admin, value, cetipIsin }) => {
    const ultimoCreditoEmissaoDefault = { data_referencia: "", evento_credito: "", score_evento: "" }
    const [openMostrarTodos, setOpenMostrarTodos] = useState(false)
    const emissaoService = new EmissaoService()
    const [ultimoCreditoEmissao, setUltimoCreditoEmissao] = useState(ultimoCreditoEmissaoDefault)
    const [loadingEventoCreditoEmissao, setLoadingEventoCreditoEmissao] = useState(false)
    const [rowsEventoCreditoEmissao, setRowsEventoCreditoEmissao] = useState([])
    const [table, setTable] = useState(defaultTable)
    const [alertMessageState, setAlertMessageState] = React.useState({
        open: false,
        message: "",
        type: ""
    })
    useEffect(() => {
        if (validateValue()) loadData(value)
    }, [value])

    const validateValue = () => {
        if (value && value.length > 4) {
            return true
        }

        return false
    }

    const loadData = (value) => {
        setLoadingEventoCreditoEmissao(true)
        emissaoService.getUltimoEventoCreditoEmissao(value).then((response) => {
            const { body } = response
            const { data } = body

            if (data !== null) {
                const { DataReferencia, EventoCreditoEmissao, ScoreEvento } = data

                setUltimoCreditoEmissao({
                    data_referencia: dayjs(DataReferencia).format(DATE_FORMAT_SHOW),
                    evento_credito: EventoCreditoEmissao,
                    score_evento: ScoreEvento,
                })
            } else {
                setUltimoCreditoEmissao(ultimoCreditoEmissaoDefault)
            }
        }).catch(() => {
            setUltimoCreditoEmissao(ultimoCreditoEmissaoDefault)
        }).finally(() => {
            setLoadingEventoCreditoEmissao(false)
        })
    }

    const buildTable = () => {
        setTable(
            [
                {
                    cells: [`CETIP: ${cetipIsin.CETIP}`, `ISIN: ${cetipIsin.ISIN}`]
                },
            ]
        )
    }

    const openEventoCreditoEmissaoModal = () => {
        setAlertMessageState({ open: false, message: "", type: "" })
        if (!validateValue()) {
            setOpenMostrarTodos(true)
            openAlert("Não há dados para o CETIP consultado.", "warning")
            return
        }

        buildTable()
        setOpenMostrarTodos(true)
        setLoadingEventoCreditoEmissao(true)
        emissaoService.eventoCreditoEmissao("get", `?CETIP=${value}`)
            .then((response) => {
                //setOpenMostrarTodos(true)
                const { body } = response
                const { results } = body
                if (!results || results.length === 0) {
                    openAlert("Não há dados para o CETIP consultado.", "warning")
                } else {
                    // Limpa o alerta se houver dados
                    setAlertMessageState({ open: false, message: "", type: "" })
                }
                const rows = ordenaPorDataReferencia(results, "desc").map((obj, index) => ({ ...obj, id: index }))
                setRowsEventoCreditoEmissao(rows)
            })
            .catch(() => {
                openAlert("Erro ao buscar os dados. Tente novamente.", "error")
            })
            .finally(() => {
                setLoadingEventoCreditoEmissao(false)
            })
    }

    const deleteItem = async (row) => {
        const { ID } = row

        setLoadingEventoCreditoEmissao(true)
        emissaoService.eventoCreditoEmissao("delete", ID)
            .then(() => { openEventoCreditoEmissaoModal() })
            .finally(() => {
                setLoadingEventoCreditoEmissao(false)
            })
    }

    const addRow = async () => {
        let lastItem = rowsEventoCreditoEmissao[0]

        if (rowsEventoCreditoEmissao.length === 0) {
            lastItem = {
                "CETIP": value,
                "DataReferencia": dayjs().format(DATE_FORMAT),
                "EventoCreditoEmissao": "N/A",
                "ScoreEvento": 0
            }
        }

        setLoadingEventoCreditoEmissao(true)
        emissaoService.eventoCreditoEmissao("post", lastItem)
            .then(() => {
                openEventoCreditoEmissaoModal()
            })
            .catch(() => { })
            .finally(() => {
                setLoadingEventoCreditoEmissao(false)
            })
    }

    const updateRow = async (newRow) => {
        const { ID } = newRow
        setLoadingEventoCreditoEmissao(true)
        emissaoService.eventoCreditoEmissao("patch", ID, newRow)
            .finally(() => {
                setLoadingEventoCreditoEmissao(false)
            })

        return newRow
    }

    const openAlert = (message, type) => {
        setAlertMessageState({
            ...alertMessageState,
            open: true,
            message: message,
            type: type
        })
    }

    const closeAlert = () => {
        setAlertMessageState({ ...alertMessageState, open: false })
    }

    const minWidthEvento = calculateMinWidth(rowsEventoCreditoEmissao, "EventoCreditoEmissao") / 2

    return <>
        {loadingEventoCreditoEmissao ? (
            <Box sx={{ minHeight: '96px' }}> 
                <SkeletonLine />
            </Box>
        ) : (
            <Grid container direction="row" alignItems={"center"} justifyContent="space-between" spacing={2}>
                {ultimoCreditoEmissao.data_referencia || ultimoCreditoEmissao.evento_credito || ultimoCreditoEmissao.score_evento ? (
                    <>
                        <Grid item xs={12} md={3} sm={12} xl={2}>
                            <TextField multiline rows={2} fullWidth label="Data Referência" value={ultimoCreditoEmissao.data_referencia} />
                        </Grid>
                        <Grid item xs={12} md={3} sm={12} xl={5}>
                            <TextField multiline rows={2} fullWidth label="Evento de Crédito" value={ultimoCreditoEmissao.evento_credito} />
                        </Grid>
                        <Grid item xs={12} md={3} sm={12} xl={2}>
                            <TextField multiline rows={2} fullWidth label="Score Evento" value={ultimoCreditoEmissao.score_evento} />
                        </Grid>
                    </>
                ) : (
                    <Grid item xs={8}>
                        <Typography variant="body2" color="black" align="center">
                            Não há evento de crédito de emissão para o CETIP consultado.
                        </Typography>
                    </Grid>
                )}
                <Grid item xs={12} md={3} sm={12} xl={3}>
                    <Button fullWidth variant="contained" onClick={openEventoCreditoEmissaoModal}>Mostrar Todos</Button>
                </Grid>

            </Grid>
        )}

        <ModalDialogPage open={openMostrarTodos}
            title="Histórico Evento de Crédito Emissão"
            close={() => {
                setOpenMostrarTodos(false)
            }}>
            <>
                <Grid container direction="column" spacing={2}>
                    <Grid item xs={12} md={12} xl={12}>
                        <TableGray data={table} />
                        <Grid item xs={12} style={{ marginTop: 8 }}>
                            <AlertMessage open={alertMessageState.open} message={alertMessageState.message} type={alertMessageState.type} close={closeAlert} />
                        </Grid>
                    </Grid>
                </Grid>
                <br />
                <Grid container direction="column">
                    <Grid item xs={12} md={12} xl={12}>
                        {loadingEventoCreditoEmissao ? <SkeletonLine /> : (
                            <ParameterTable
                                columns={[
                                    { field: 'DataReferencia', headerName: 'Data Referencia', minWidth: 150, flex: 1, editable: admin, renderCell: renderCellDate },
                                    { field: 'EventoCreditoEmissao', headerName: 'Evento', flex: 1, minWidth: minWidthEvento, editable: admin, renderCell: renderTextColumn },
                                    { field: 'ScoreEvento', headerName: 'Score Evento', editable: admin, flex: 1, minWidth: 120 },
                                ]}
                                rowHeigth="auto"
                                data={rowsEventoCreditoEmissao}
                                setData={setRowsEventoCreditoEmissao}
                                createRow={addRow}
                                deleteRow={deleteItem}
                                updateRow={updateRow}
                                loadData={loadData}
                                disableDeleteColumn={!admin}
                                disableAddRow={!admin}
                                buttonAddVariant={"contained"}
                                buttonAddTitle={"Adicionar Rating"}
                            />
                        )}
                    </Grid>
                </Grid>
            </>
        </ModalDialogPage>
    </>
}

UltimoEventoCreditoEmissao.propTypes = {
    admin: PropTypes.bool,
    cetipIsin: PropTypes.object,
    value: PropTypes.string,
    dadosEmpresa: PropTypes.object,
    loading: PropTypes.func,
}

export default UltimoEventoCreditoEmissao